import {
  LocationAlarmUpdate,
  ModifyAlarmUpdateEntry,
} from '@hakimo-ui/hakimo/types';
import clsx from 'clsx';
import { ComponentType, ReactNode, RefObject } from 'react';
import {
  EditState,
  OUTLINE_CLASS,
  SITUATION_OPTIONS,
  derivedUpdateType,
} from '../../sop-workflow/util';
import { DispatchTimelineEntryEdit } from './DispatchTimelineEntryEdit';
import { TimelineEntryViewProps } from './useTimelineEntryView';

export interface DispatchTimelineRowProps {
  ev: LocationAlarmUpdate;
  pending: Partial<ModifyAlarmUpdateEntry>;
  pendingFiles?: File[];
  inEdit: boolean;
  editState: EditState | null;
  isLast: boolean;
  timezone?: string;
  fileInputRef: RefObject<HTMLInputElement>;
  editDisabled?: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  patch: (p: Partial<EditState>) => void;
}

interface Props extends DispatchTimelineRowProps {
  View: ComponentType<TimelineEntryViewProps>;
  largeEditor: boolean;
  dotRowClass: string;
  timestampColumn?: ReactNode;
}

export function DispatchTimelineRowChrome({
  ev,
  pending: p,
  pendingFiles = [],
  inEdit,
  editState: s,
  isLast,
  timezone: tz,
  fileInputRef,
  editDisabled = false,
  onEdit,
  onSave,
  onCancel,
  patch,
  View,
  largeEditor,
  dotRowClass,
  timestampColumn,
}: Props) {
  const effectiveType = derivedUpdateType(
    p.update_type ?? (ev.update_type as string)
  );
  const dotClass =
    SITUATION_OPTIONS.find((o) => o.value === effectiveType)?.dotClass ??
    'bg-status-orange';

  return (
    <div className="flex items-stretch gap-3">
      {timestampColumn}
      <div className="flex flex-col items-center">
        <div className={clsx('flex items-center', dotRowClass)}>
          <div
            className={clsx(
              'h-2.5 w-2.5 shrink-0 rounded-full',
              dotClass,
              inEdit &&
                `outline outline-2 outline-offset-2 ${OUTLINE_CLASS[effectiveType]}`
            )}
          />
        </div>
        {!isLast && (
          <div className="bg-onlight-line-2 dark:bg-ondark-line-2 mt-1 w-px flex-1" />
        )}
      </div>

      <div className={clsx('min-w-0 flex-1', !isLast && 'pb-4')}>
        {!inEdit && (
          <View
            ev={ev}
            pending={p}
            pendingFiles={pendingFiles}
            timezone={tz}
            editDisabled={editDisabled}
            onEdit={onEdit}
          />
        )}

        {inEdit && s && (
          <DispatchTimelineEntryEdit
            ev={ev}
            pending={p}
            editState={s}
            timezone={tz}
            fileInputRef={fileInputRef}
            onSave={onSave}
            onCancel={onCancel}
            patch={patch}
            large={largeEditor}
          />
        )}
      </div>
    </div>
  );
}
